import React, { useMemo, useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { ArrowDropUp, ArrowDropDown, UnfoldMore } from "@mui/icons-material";
import { getPreference, setPreference } from "../../Utils/userPreferenceManger";
import { tokens, withAlpha } from "../../Common/designTokens"; // NEW
import { utcToLocal } from "../../Utils/timeConversions";

const rowHeight = "40px";
const cellPadding = "4px 8px";

const formatDateToDDMonYY = (dateStr) => {
  const localDt = utcToLocal(dateStr);
  return localDt ? localDt.format("DMMMYY HH:mm") : "---";
};

const CounterPartyTable = ({ data, onEdit, onDelete, onSort, sortConfig, loading = false }) => {
  const columns = useMemo(
    () => [
      // default widths requested (used when no saved preference)
      { key: "counterPartyName", name: "Name", width: 363, minWidth: 120 },
      { key: "description", name: "Description", width: 726, minWidth: 100 },
      { key: "updatedAt", name: "Updated At", width: 170, minWidth: 140 },
      { key: "action", name: "Action", width: 194, minWidth: 160 },
    ],
    []
  );

  // Resizable widths like BondTable
  const SECTION_KEY = "counter-parties";
  const [columnWidths, setColumnWidths] = useState({});
  const resizingColumnRef = useRef(null);
  const initialWidthRef = useRef(0);
  const initialXRef = useRef(0);

  // Initialize widths from saved prefs or defaults from columns
  useEffect(() => {
    try {
      const saved = getPreference(`columnWidths_${SECTION_KEY}`);
      if (saved && typeof saved === "object") {
        setColumnWidths(saved);
      } else {
        // Use explicit requested defaults mapped to column keys (persist once)
        const defaults = {
          counterPartyName: 363,
          description: 726,
          updatedAt: 170,
          action: 194,
        };
        // fallback for any extra/unknown columns
        columns.forEach(c => {
          if (!(c.key in defaults)) defaults[c.key] = c.width || 150;
        });
        setColumnWidths(defaults);
        setPreference(`columnWidths_${SECTION_KEY}`, defaults);
      }
    } catch (e) {
      // On error, still ensure the requested defaults are used
      const defaults = {
        counterPartyName: 363,
        description: 726,
        updatedAt: 170,
        action: 194,
      };
      columns.forEach(c => {
        if (!(c.key in defaults)) defaults[c.key] = c.width || 150;
      });
      setColumnWidths(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.length]);

  // Persist on change
  useEffect(() => {
    if (columnWidths && Object.keys(columnWidths).length) {
      setPreference(`columnWidths_${SECTION_KEY}`, columnWidths);
    }
  }, [columnWidths]);

  // Min widths per column (mirrors column config)
  const minWidthMap = useMemo(() => {
    const map = {};
    columns.forEach(c => { map[c.key] = c.minWidth ?? 80; });
    return map;
  }, [columns]);

  // Max widths per column (clamp like BondTable to avoid runaway scroll)
  const maxWidthMap = useMemo(() => {
    return {
      counterPartyName: 300,
      description: 600,
      updatedAt: 240,
      action: 240,
      // default fallback
      _default: 500
    };
  }, []);

  const getClamp = (key) => {
    const minW = minWidthMap[key] || 80;
    const maxW = maxWidthMap[key] || maxWidthMap._default;
    return { minW, maxW };
  };

  const startResizing = (e, columnKey) => {
    e.stopPropagation();
    resizingColumnRef.current = columnKey;
    initialWidthRef.current = columnWidths[columnKey] || columns.find(c => c.key === columnKey)?.width || 150;
    initialXRef.current = e.clientX;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
  };

  const handleResizing = (e) => {
    if (!resizingColumnRef.current) return;
    const delta = e.clientX - initialXRef.current;
    const key = resizingColumnRef.current;
    const { minW, maxW } = getClamp(key);
    const next = Math.max(minW, Math.min(initialWidthRef.current + delta, maxW));
    setColumnWidths(prev => ({ ...prev, [key]: next }));
  };

  const stopResizing = () => {
    resizingColumnRef.current = null;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "";
  };

  const handleSortClick = (key) => {
    if (key === "action") return;
    const newDir = sortConfig?.key === key && sortConfig?.direction === "asc" ? "desc" : "asc";
    onSort(key, newDir);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        height: "100%",
        maxHeight: "100%",
        borderRadius: "0px", // CHANGED
        overflow: "auto",
        position: "relative",
        backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
        "& .MuiTableCell-root": {
          borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
        },
        "& .vertical-sticky-header": {
          position: "sticky",
          top: 0,
          backgroundColor: tokens.colors.defaultGridHeaderBackground, // CHANGED
          zIndex: 4,
        },
      }}
    >
      <Table stickyHeader sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                className="vertical-sticky-header"
                sx={{
                  backgroundColor: tokens.colors.defaultGridHeaderBackground, // CHANGED
                  color: tokens.colors.headerFontColour, // CHANGED
                  padding: cellPadding,
                  fontWeight: 700,
                  height: rowHeight,
                  userSelect: "none",
                  borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                  width: columnWidths[col.key] || col.width,
                  minWidth: col.minWidth || undefined,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      col.key === "action"
                        ? "center"
                        : col.key === "updatedAt"
                          ? "flex-end"
                          : "flex-start",
                    height: "100%",
                    width: "100%",
                    pr: "5px",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "4px",
                    }}
                    title={col.name}
                  >
                    {col.name}
                  </span>
                  {col.key !== "action" && (
                    <span
                      onClick={() => handleSortClick(col.key)}
                      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                    >
                      {sortConfig?.key === col.key ? (
                        sortConfig?.direction === "asc" ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />
                      ) : (
                        <UnfoldMore fontSize="small" sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }} />
                      )}
                    </span>
                  )}
                </Box>
                {/* Resize handle */}
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: "5px",
                    backgroundColor: tokens.colors.bodyBorderColor50, // CHANGED
                    cursor: "col-resize",
                  }}
                  onMouseDown={(e) => startResizing(e, col.key)}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {!loading &&
            data.map((row, idx) => (
              <TableRow
                key={row.counterPartyMstId || idx}
                sx={{
                  height: rowHeight,
                  "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor }, // CHANGED
                }}
              >
                <TableCell
                  sx={{
                    height: rowHeight,
                    padding: cellPadding,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
                    color: tokens.colors.fontColour, // CHANGED
                    width: columnWidths["counterPartyName"] || columns.find(c => c.key === "counterPartyName")?.width || 180,
                    minWidth: columns.find(c => c.key === "counterPartyName")?.minWidth || 120,
                  }}
                  title={row.counterPartyName}
                >
                  {row.counterPartyName || "---"}
                </TableCell>
                <TableCell
                  sx={{
                    height: rowHeight,
                    padding: cellPadding,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
                    color: tokens.colors.fontColour, // CHANGED
                    width: columnWidths["description"] || columns.find(c => c.key === "description")?.width || 360,
                    minWidth: columns.find(c => c.key === "description")?.minWidth || 100,
                  }}
                  title={row.description}
                >
                  {row.description || "---"}
                </TableCell>
                <TableCell
                  sx={{
                    height: rowHeight,
                    padding: cellPadding,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
                    color: tokens.colors.fontColour, // CHANGED
                    textAlign: "right",
                    width: columnWidths["updatedAt"] || columns.find(c => c.key === "updatedAt")?.width || 160,
                    minWidth: columns.find(c => c.key === "updatedAt")?.minWidth || 140,
                  }}
                >
                  {formatDateToDDMonYY(row.updatedAt)}
                </TableCell>
                <TableCell
                  sx={{
                    height: rowHeight,
                    padding: cellPadding,
                    backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
                    color: tokens.colors.fontColour, // CHANGED
                    textAlign: "center",
                    width: columnWidths["action"] || columns.find(c => c.key === "action")?.width || 200,
                    minWidth: columns.find(c => c.key === "action")?.minWidth || 160,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onEdit(row)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        color: tokens.palette.primary,
                        borderColor: tokens.palette.primary,
                        "&:hover": {
                          borderColor: tokens.palette.primary,
                          backgroundColor: withAlpha(tokens.palette.primary, 0.08),
                        },
                      }}
                    >
                      Update
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => onDelete(row)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderColor: tokens.palette.danger,
                        color: tokens.palette.danger,
                        "&:hover": {
                          borderColor: tokens.palette.danger,
                          backgroundColor: withAlpha(tokens.palette.danger, 0.08),
                        },
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

          {!loading && data.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{
                  textAlign: "center",
                  backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
                  color: tokens.colors.fontColour80, // CHANGED
                  height: 200,
                }}
              >
                No counterparties found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: rowHeight,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            background: "transparent",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </TableContainer>
  );
};

export default CounterPartyTable;
